import React, { useState } from "react";
import { Mail, Phone, MessageCircle, Send } from "lucide-react";
import { Reveal, MaskLines, LiveDot } from "./ui/Primitives";

const WHATSAPP = "972557070007";

interface FormState {
  name: string;
  phone: string;
  email: string;
  message: string;
}

type Errors = Partial<Record<keyof FormState, string>>;

const initial: FormState = { name: "", phone: "", email: "", message: "" };

const validate = (form: FormState): Errors => {
  const errors: Errors = {};
  if (!form.name.trim()) errors.name = "צריך שם כדי לדעת למי לחזור";
  if (!form.phone.trim()) {
    errors.phone = "צריך טלפון כדי לחזור אליכם";
  } else if (!/^[\d\s+()-]{9,}$/.test(form.phone.trim())) {
    errors.phone = "מספר הטלפון לא נראה תקין";
  }
  if (form.email.trim() && !/^\S+@\S+\.\S+$/.test(form.email.trim())) {
    errors.email = "כתובת האימייל לא נראית תקינה";
  }
  if (!form.message.trim()) errors.message = "כתבו משפט אחד על מה שאתם צריכים";
  return errors;
};

const contactMethods = [
  {
    icon: Phone,
    label: "טלפון",
    value: "055-7070007",
    href: "tel:+972557070007",
    note: "התקשרו ונדבר",
    ltr: true,
  },
  {
    icon: MessageCircle,
    label: "וואטסאפ",
    value: "שלחו הודעה",
    href: `https://wa.me/${WHATSAPP}`,
    note: "תגובה תוך דקות",
    ltr: false,
  },
  {
    icon: Mail,
    label: "אימייל",
    value: "info@eladkeren.com",
    href: "mailto:info@eladkeren.com",
    note: "כתבו לנו",
    ltr: true,
  },
];

const promises = [
  "ליווי אישי ומקצועי מהצעד הראשון",
  "שקיפות מלאה ודוחות שוטפים",
  "תוצאות מוכחות ומדידות",
  "זמינות לכל שאלה",
];

const CTA: React.FC = () => {
  const [form, setForm] = useState<FormState>(initial);
  const [errors, setErrors] = useState<Errors>({});
  const [submitted, setSubmitted] = useState(false);

  const update = (key: keyof FormState) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setForm((f) => ({ ...f, [key]: e.target.value }));
    // Clear a field's error the moment the visitor starts fixing it.
    setErrors((prev) => (prev[key] ? { ...prev, [key]: undefined } : prev));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const found = validate(form);
    setErrors(found);
    if (Object.keys(found).length > 0) {
      const firstKey = Object.keys(found)[0];
      document.getElementById(`field-${firstKey}`)?.focus();
      return;
    }

    const lines = [
      `שלום, שמי ${form.name.trim()}.`,
      form.message.trim(),
      `טלפון: ${form.phone.trim()}`,
      form.email.trim() ? `אימייל: ${form.email.trim()}` : "",
      `נשלח מ-eladdigital.com`,
    ].filter(Boolean);

    window.open(
      `https://wa.me/${WHATSAPP}?text=${encodeURIComponent(lines.join("\n"))}`,
      "_blank",
      "noopener"
    );
    setSubmitted(true);
  };

  const fieldClass = (key: keyof FormState) =>
    `w-full border bg-surface px-4 py-3 text-fg placeholder:text-fg-3 transition-colors duration-200 focus:outline-none focus-visible:border-signal ${
      errors[key] ? "border-[#ff6b5e]" : "border-[var(--line-strong)]"
    }`;

  return (
    <section id="contact" className="bg-ink pb-24 pt-28 lg:pb-32 lg:pt-36">
      <div className="shell">
        <MaskLines
          as="h1"
          className="t-display max-w-[12ch] text-fg"
          lines={["מוכנים", "לשלב הבא?"]}
        />

        <Reveal delay={140}>
          <p className="t-lead mt-8 max-w-[46ch]">
            השאירו פרטים ונחזור אליכם תוך שעות ספורות עם הצעה מותאמת אישית.
          </p>
        </Reveal>

        {/* Direct routes first, for anyone who does not want a form */}
        <Reveal delay={200}>
          <dl className="mt-14 grid gap-px border border-[var(--line)] bg-[var(--line)] sm:grid-cols-3">
            {contactMethods.map((method) => {
              const Icon = method.icon;
              const external = method.href.startsWith("http");
              return (
                <a
                  key={method.label}
                  href={method.href}
                  {...(external
                    ? { target: "_blank", rel: "noopener noreferrer" }
                    : {})}
                  className="group bg-ink p-6 transition-colors duration-200 hover:bg-surface"
                >
                  <dt className="t-meta mb-3 flex items-center gap-2">
                    <Icon className="h-3.5 w-3.5" aria-hidden="true" />
                    {method.label}
                  </dt>
                  <dd
                    className="text-lg text-fg transition-colors group-hover:text-signal"
                    {...(method.ltr ? { dir: "ltr" as const } : {})}
                  >
                    {method.value}
                  </dd>
                  <dd className="mt-1 text-sm text-fg-3">{method.note}</dd>
                </a>
              );
            })}
          </dl>
        </Reveal>

        <div className="mt-20 grid gap-14 lg:grid-cols-[1.4fr_1fr] lg:gap-20">
          <Reveal>
            <form onSubmit={handleSubmit} noValidate>
              <h2 className="t-h2 mb-8 text-fg">ספרו לנו על הפרויקט</h2>

              <div className="grid gap-5 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <label htmlFor="field-name" className="t-meta mb-2 block">
                    שם מלא *
                  </label>
                  <input
                    id="field-name"
                    name="name"
                    type="text"
                    autoComplete="name"
                    value={form.name}
                    onChange={update("name")}
                    aria-invalid={Boolean(errors.name)}
                    aria-describedby={errors.name ? "err-name" : undefined}
                    className={fieldClass("name")}
                    placeholder="הכניסו את שמכם"
                  />
                  {errors.name && (
                    <p id="err-name" className="mt-2 text-sm text-[#ff8b80]">
                      {errors.name}
                    </p>
                  )}
                </div>

                <div>
                  <label htmlFor="field-phone" className="t-meta mb-2 block">
                    טלפון *
                  </label>
                  <input
                    id="field-phone"
                    name="phone"
                    type="tel"
                    dir="ltr"
                    autoComplete="tel"
                    value={form.phone}
                    onChange={update("phone")}
                    aria-invalid={Boolean(errors.phone)}
                    aria-describedby={errors.phone ? "err-phone" : undefined}
                    className={`${fieldClass("phone")} text-right`}
                    placeholder="050-0000000"
                  />
                  {errors.phone && (
                    <p id="err-phone" className="mt-2 text-sm text-[#ff8b80]">
                      {errors.phone}
                    </p>
                  )}
                </div>

                <div>
                  <label htmlFor="field-email" className="t-meta mb-2 block">
                    אימייל
                  </label>
                  <input
                    id="field-email"
                    name="email"
                    type="email"
                    dir="ltr"
                    autoComplete="email"
                    value={form.email}
                    onChange={update("email")}
                    aria-invalid={Boolean(errors.email)}
                    aria-describedby={errors.email ? "err-email" : undefined}
                    className={`${fieldClass("email")} text-right`}
                    placeholder="you@company.com"
                  />
                  {errors.email && (
                    <p id="err-email" className="mt-2 text-sm text-[#ff8b80]">
                      {errors.email}
                    </p>
                  )}
                </div>

                <div className="sm:col-span-2">
                  <label htmlFor="field-message" className="t-meta mb-2 block">
                    על מה מדובר *
                  </label>
                  <textarea
                    id="field-message"
                    name="message"
                    rows={5}
                    value={form.message}
                    onChange={update("message")}
                    aria-invalid={Boolean(errors.message)}
                    aria-describedby={errors.message ? "err-message" : undefined}
                    className={`${fieldClass("message")} resize-y`}
                    placeholder="אתר חדש, אפליקציה, דשבורד, קידום אורגני..."
                  />
                  {errors.message && (
                    <p id="err-message" className="mt-2 text-sm text-[#ff8b80]">
                      {errors.message}
                    </p>
                  )}
                </div>
              </div>

              <button type="submit" className="btn btn-primary mt-8 w-full sm:w-auto">
                <Send className="h-4 w-4" />
                <span>שליחה בוואטסאפ</span>
              </button>

              <p className="mt-4 text-sm text-fg-3">
                הכפתור פותח וואטסאפ עם ההודעה מוכנה. נחזור אליכם תוך שעות ספורות,
                ללא התחייבות.
              </p>

              {/* Confirmation, since the form hands off to another app */}
              <p
                role="status"
                aria-live="polite"
                className={`mt-4 flex items-center gap-2.5 text-sm text-signal transition-opacity duration-300 ${
                  submitted ? "opacity-100" : "opacity-0"
                }`}
              >
                <LiveDot />
                {submitted
                  ? "נפתח וואטסאפ עם ההודעה. אם החלון נחסם, לחצו על כפתור הוואטסאפ למעלה."
                  : " "}
              </p>
            </form>
          </Reveal>

          <Reveal delay={140}>
            <div className="border-t border-[var(--line)] pt-6">
              <h2 className="t-h3 mb-6 text-fg">מה אתם מקבלים</h2>
              <ul className="space-y-4">
                {promises.map((promise) => (
                  <li key={promise} className="flex items-start gap-3 text-fg-2">
                    <span
                      aria-hidden="true"
                      className="mt-[0.7em] h-px w-4 shrink-0 bg-signal"
                    />
                    <span>{promise}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-10 flex items-center gap-2.5 border-t border-[var(--line)] pt-6">
                <LiveDot />
                <span className="t-meta">זמין עכשיו</span>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default CTA;
